// 업로드 step 1 의 "예시 사진" — 사진이 없는 사용자도 흐름을 끝까지 타볼 수
// 있게 하는 진입점. 탭하면 full 을 fetch 해서 blob 을 그대로 업로드 슬롯에
// 넣는다 (압축·검증은 App.jsx 의 processAndAddImage → CameraService 담당).
// 생성 자체는 평소와 똑같이 크레딧을 쓴다 — 결과를 미리 만들어 두는 게
// 아니라 "내가 올린 사진" 자리에 대신 넣어 주는 것뿐이다.
//
// 항목마다 thumb / full 두 장을 둔다. 목록에 그리는 건 104px 칸인데 업로드용
// 원본을 그대로 걸면 화면 한 번 여는 데 몇 MB 가 나간다.
//   thumb — 320px (3x 디스플레이까지 커버), 목록 표시 전용
//   full  — 1024px, 탭했을 때만 받는 실제 입력 이미지
//
// 키는 SPACE_CATEGORIES (services/ai-service.js) 와 같아야 한다.
// 배열이 비어 있는 카테고리는 UploadStep 이 섹션 자체를 렌더하지 않는다.
//
// ── 사진을 고르는 기준 ──────────────────────────────────────────────
// 전부 Higgsfield GPT Image 2 로 만든 합성 이미지고, **깨끗하고 밝은 공간**
// 으로만 채운다. 처음엔 "before 니까 후줄근해야 변신이 극적이다" 로 잡고
// 낡은 집·방치된 마당을 넣었는데, 예시 사진은 앱을 처음 여는 사람이 보는
// 화면이라 그게 앱 자체를 후져 보이게 만들었다. 되돌리지 말 것.
//
// 단, "깨끗함" 을 "전부 같은 톤" 으로 만들지 말 것. 한 카테고리 안에서는
// 종류(방 용도 / 집 재료 / 마당 형태)와 밝기가 서로 갈려야 한다 — 목록이
// 한눈에 들어오는 자리라 비슷한 사진이 이어지면 여섯 장이 한 장처럼 보인다.
// 빈 방과 꾸민 방도 섞는다 ("빈 방이어도 되고 이미 꾸며진 방이어도 된다").
export const EXAMPLE_PHOTOS = {
  // 순서에 의미가 있다. 두 가지를 동시에 맞췄다.
  //
  // 1) 밝기로 묶기. 이 줄은 104px 썸네일 줄이라 그 크기에서
  //    어두운 사진은 "무드 있는 공간" 이 아니라 검은 덩어리로 읽힌다.
  //    밝고 어두운 걸 번갈아 놓았더니 줄 가운데 구멍이 뚫린 것처럼 보였다.
  //    실측 밝기(지각 luminance)는 밝은 쪽 139~199, 어두운 쪽 27~58 로
  //    가운데가 비어 있다 — 그 절벽이 붙어 있는 칸 사이에 오면 안 된다.
  //    밝은 여섯 장을 앞에, 어두운 네 장을 뒤에 몰아 절벽을 6↔7 로 보낸다.
  //    (색온도는 r-b 가 전부 +21~44 라 이미 균일하다. 밝기만 문제였다.)
  //
  // 2) 상업 공간을 보이는 자리에. 390pt 폭 기준 완전히 보이는 건 세 칸
  //    뿐이다(4번째는 30px 만 걸친다). 전부 집이면 "카페나 가게도 된다" 가
  //    아예 안 보이므로 카페를 3번에 둔다. 뒤로 밀지 말 것.
  //
  // 밝기 순(참고): 199 / 160 / 139 / 183 / 183 / 152 │ 58 / 55 / 47 / 27
  // 앞 세 칸이 199→160→139 로 단조 감소하고, 2·3번이 편집숍·카페라
  // 상업 공간 신호가 스크롤 없이 두 칸이나 보인다.
  interior: [
    'empty-living-room',
    'boutique-shop',
    'cafe',
    'minimal-kitchen',
    'tiled-bathroom',
    'linen-sofa-living-room',
    'restaurant',
    'walnut-study',
    'dusk-bedroom',
    'wine-bar',
  ].map(toEntry('interior')),

  // 밝기 폭이 117~154 로 좋은 종이라 interior 같은 절벽이 없다.
  // 그래서 여긴 밝기보다 재료가 기준 — 보이는 세 칸이 흰 스터코 /
  // 나무 / 벨5돌 로 갈리게 둔다. 가장 어두운 차콜 팀버만 맨 뒤로.
  exterior: [
    'white-two-story',
    'wood-clad-single-story',
    'white-brick-townhouse',
    'nordic-gabled-house',
    'mediterranean-villa',
    'charcoal-timber-house',
  ].map(toEntry('exterior')),

  // 밝기 96~148. 밝은 둘을 앞에, 가장 어두운 데크를 맨 뒤로.
  // 보이는 세 칸은 중정 / 옥상 / 잔디마당 으로 마당 형태가 갈리게.
  garden: [
    'gravel-courtyard',
    'rooftop-terrace',
    'backyard-lawn',
    'stone-path-beds',
    'pergola-patio',
    'wood-deck',
  ].map(toEntry('garden')),
};

// public/examples/<카테고리>/<이름>.jpg + .../thumb/<이름>.jpg 규칙.
// 사진을 추가할 땐 두 파일을 그 규칙대로 넣고 위 배열에 이름만 더하면 된다.
function toEntry(category) {
  return (name) => ({
    thumb: `/examples/${category}/thumb/${name}.jpg`,
    full: `/examples/${category}/${name}.jpg`,
  });
}

export function getExamplePhotos(spaceCategory) {
  return EXAMPLE_PHOTOS[spaceCategory] || [];
}
